import React, { useCallback, useEffect, useRef } from 'react';
import Lottie from 'lottie-react';
import { Message } from '../types/chat';
import { UnifiedMessageCard } from './UnifiedMessageCard';
import useAuth from '@/contexts/useAuth';
import {
  ADA_TAGLINE,
  FAQ_QUESTIONS,
  SUPER_PLUS_EXPLAINER,
} from '@/utils/constants';
import loadingAnimation from '@/assets/loading-spinner.json';
import logo from '@/assets/logo.webp';

interface MessageListProps {
  messages: Message[];
  sessionId: string;
  isLoading: boolean;
  showTraces?: boolean;
  autoScroll?: boolean;
  onQuestionClick?: (question: string) => void;
}

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  sessionId,
  isLoading,
  showTraces = true,
  autoScroll = true,
  onQuestionClick,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { user } = useAuth();
  console.log('User in MessageList:', user);

  const handleQuestionClick = (question: string) => {
    if (onQuestionClick) {
      onQuestionClick(question);
    }
  };

  const scrollToUserMessage = useCallback(() => {
    if (containerRef.current && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];

      if (lastMessage?.sender === 'user') {
        // For user messages, scroll smoothly to bottom
        setTimeout(() => {
          containerRef.current?.scrollTo({
            top: containerRef.current.scrollHeight,
            behavior: 'smooth',
          });
        }, 0);
      } else {
        // For AI messages, find the last user message and scroll to it
        let lastUserMessageIndex = -1;
        for (let i = messages.length - 1; i >= 0; i--) {
          if (messages[i].sender === 'user') {
            lastUserMessageIndex = i;
            break;
          }
        }

        if (lastUserMessageIndex !== -1) {
          // Get the message element and scroll to position it at the top
          const messageElements = containerRef.current.querySelectorAll(
            '[data-message-index]',
          );
          const userMessageElement = messageElements[
            lastUserMessageIndex
          ] as HTMLElement;

          if (userMessageElement) {
            const containerTop = containerRef.current.offsetTop;
            const messageTop = userMessageElement.offsetTop;

            containerRef.current.scrollTo({
              top: messageTop - containerTop - 16, // 16px padding
              behavior: 'smooth',
            });
          }
        }
      }
    }
  }, [messages]);

  useEffect(() => {
    if (autoScroll) {
      scrollToUserMessage();
    }
  }, [messages, autoScroll, scrollToUserMessage]);

  const WelcomeMessage = () => (
    <>
      <div className="flex flex-col items-center justify-center w-full max-w-xl p-4 text-center border shadow-sm shadow-teal-500/50">
        <div>
          <div>
            <img
              src={logo}
              alt="Allianz"
              className="h-12 w-auto"
              onError={(e) => {
                // Hide logo if not found
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        </div>

        <h2 className="text-xl text-gray-400 mb-4">{ADA_TAGLINE}</h2>
      </div>
      <div className="mt-4 space-y-4">
        <div className="text-center">
          <p className="inline-block max-w-xl px-3 py-2 sm:px-4 sm:py-3 text-sm bg-teal-50 text-gray-700 rounded-lg border border-teal-200">
            {SUPER_PLUS_EXPLAINER}
          </p>
        </div>
        <div className="text-center mb-4">
          <p className="inline-block text-center px-3 py-2 sm:px-4 sm:py-3 text-sm bg-blue-50 text-gray-700 rounded-lg">
            Unsure of where to start? Here's some questions that other users
            have asked...
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left Column - New User Questions */}
          <div className="flex flex-col items-center space-y-2">
            <h3 className="text-md font-medium text-gray-700 mb-3 text-center">
              🌟 New? Try asking...
            </h3>
            {FAQ_QUESTIONS.NEW_USER.map((question, index) => (
              <button
                key={index}
                onClick={() => handleQuestionClick(question)}
                className="inline-block text-center px-4 py-3 text-sm bg-teal-50 hover:bg-teal-100 text-gray-700 hover:text-teal-800 rounded-full border border-teal-200 hover:border-teal-300 transition-all duration-200 hover:shadow-md"
              >
                <span className="font-medium">{question}</span>
              </button>
            ))}
          </div>

          {/* Right Column - Experience User Questions */}
          <div className="flex flex-col items-center space-y-2">
            <h3 className="text-md font-medium text-gray-700 mb-3 text-center">
              🚀 Seasoned pro? Perhaps ask...
            </h3>
            {FAQ_QUESTIONS.EXPERIENCED_USER.map((question, index) => (
              <button
                key={index}
                onClick={() => handleQuestionClick(question)}
                className="inline-block text-center px-4 py-3 text-sm bg-teal-50 hover:bg-teal-100 text-gray-700 hover:text-teal-800 rounded-full border border-teal-200 hover:border-teal-300 transition-all duration-200 hover:shadow-md"
              >
                <span className="font-medium">{question}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="text-center mt-4">
          <p className="inline-block text-center px-3 py-2 sm:px-4 sm:py-3 text-sm bg-blue-50 text-gray-700 rounded-lg">
            Alternatively, type your own question in the input bar below.
          </p>
        </div>
      </div>
    </>
  );

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-start sm:justify-center h-full pt-6 sm:pt-0">
      <WelcomeMessage />
    </div>
  );

  const LoadingIndicator = () => {
    // Check if we have any streaming messages with specific progress text
    const streamingMessage = messages.find(
      (msg) => msg.isLoading && msg.type === 'streaming',
    );
    const loadingText = streamingMessage?.content || 'AI is thinking...';

    return (
      <div className="flex justify-center py-4">
        <div className="flex flex-col items-center gap-1">
          <Lottie
            animationData={loadingAnimation}
            loop={true}
            style={{ width: 30, height: 30 }}
          />
          <span className="text-sm text-gray-500">{loadingText}</span>
        </div>
      </div>
    );
  };

  if (messages.length === 0 && !isLoading) {
    return (
      <div ref={containerRef} className="flex-1 overflow-y-auto bg-gray-50 p-4">
        <EmptyState />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto bg-gray-50 p-4 space-y-1"
    >
      {/* Message list */}
      <div className="max-w-4xl mx-auto">
        {messages.map((message, index) => (
          <div key={message.id} data-message-index={index}>
            <UnifiedMessageCard
              sessionId={sessionId}
              message={message}
              // Use message.id (set at creation time) as the DynamoDB conversation
              // array index — more reliable than the UI array index which can drift
              // if error/system messages are inserted between stored turns.
              messageIndex={Number(message.id)}
              showDebug={showTraces}
            />
          </div>
        ))}

        {/* Loading indicator - show when loading OR when there are streaming messages */}
        {(isLoading ||
          messages.some(
            (msg) => msg.isLoading && msg.type === 'streaming',
          )) && <LoadingIndicator />}
      </div>

      {/* Scroll anchor */}
      <div ref={messagesEndRef} />
    </div>
  );
};
